import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, format, isSameMonth, isSameDay, isToday,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, MapPin } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../../../components/shared/Modal';

const DURACOES = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hora' },
  { value: 90, label: '1h30' },
  { value: 120, label: '2 horas' },
];

// Cor por origem do compromisso: visitas (eventTypeId 4/6, criadas pelo funil/módulo de
// visitas) ficam roxo, o resto (compromissos livres, "Secret meeting", etc) fica azul.
function corDoEvento(eventTypeId) {
  return [4, 6].includes(eventTypeId) ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';
}

function NovoCompromissoModal({ isOpen, onClose, dataInicial }) {
  const qc = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [data, setData] = useState(dataInicial || '');
  const [hora, setHora] = useState('');
  const [duracao, setDuracao] = useState(60);
  const [local, setLocal] = useState('');
  const [observacao, setObservacao] = useState('');

  React.useEffect(() => { if (isOpen) setData(dataInicial || ''); }, [isOpen, dataInicial]);

  const criarMut = useMutation({
    mutationFn: () => api.post('/agenda/compromissos', {
      titulo, data, hora, duracao_minutos: duracao, local, observacao: observacao || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compromissos'] });
      onClose();
      setTitulo(''); setHora(''); setDuracao(60); setLocal(''); setObservacao('');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo compromisso" size="sm">
      <form onSubmit={e => { e.preventDefault(); criarMut.mutate(); }} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Título</label>
          <input
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            className="input-field w-full"
            placeholder="Ex: Reunião na Only Design"
            required
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data</label>
            <input type="date" value={data} onChange={e => setData(e.target.value)} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Hora</label>
            <input type="time" value={hora} onChange={e => setHora(e.target.value)} className="input-field w-full" required />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Duração</label>
          <select value={duracao} onChange={e => setDuracao(Number(e.target.value))} className="input-field w-full">
            {DURACOES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Local</label>
          <input
            value={local}
            onChange={e => setLocal(e.target.value)}
            className="input-field w-full"
            placeholder="Ex: Escritório, endereço, online..."
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observação</label>
          <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={2} className="input-field w-full resize-y" />
        </div>
        <p className="text-xs text-gray-400">Sincroniza automaticamente com o Google Agenda e o Apple Agenda.</p>
        {criarMut.isError && (
          <p className="text-sm text-red-600">{criarMut.error?.response?.data?.error || 'Erro ao criar compromisso'}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={criarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
            {criarMut.isPending ? 'Agendando...' : 'Agendar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DiaDetalheModal({ isOpen, onClose, dia, eventos }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={dia ? format(dia, "dd 'de' MMMM", { locale: ptBR }) : ''} size="sm">
      <div className="space-y-2">
        {(eventos || []).length === 0 && <p className="text-sm text-gray-400">Nenhum compromisso nesse dia.</p>}
        {(eventos || []).map(ev => (
          <div key={ev.id} className="border border-gray-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-gray-800">{ev.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {format(new Date(ev.startTime), 'HH:mm')} – {format(new Date(ev.endTime), 'HH:mm')}
            </p>
            {ev.location && (
              <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {ev.location}</p>
            )}
            <a
              href={`https://agenda.minhainbase.com.br/booking/${ev.uid}`}
              target="_blank" rel="noreferrer"
              className="text-xs text-primary hover:underline mt-1 inline-block"
            >
              Ver no cal.diy
            </a>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function MinhaAgenda() {
  const [mesRef, setMesRef] = useState(new Date());
  const [novoOpen, setNovoOpen] = useState(false);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [dataParaNovo, setDataParaNovo] = useState('');

  const inicioGrid = startOfWeek(startOfMonth(mesRef), { weekStartsOn: 0 });
  const fimGrid = endOfWeek(endOfMonth(mesRef), { weekStartsOn: 0 });
  const dias = eachDayOfInterval({ start: inicioGrid, end: fimGrid });

  const { data: compromissos, isLoading } = useQuery({
    queryKey: ['compromissos', format(startOfMonth(mesRef), 'yyyy-MM')],
    queryFn: () => api.get('/agenda/compromissos', {
      params: { inicio: startOfMonth(mesRef).toISOString(), fim: addMonths(startOfMonth(mesRef), 1).toISOString() },
    }).then(r => r.data.data),
    refetchInterval: 30000,
  });

  const eventosNoDia = (dia) => (compromissos || []).filter(ev => isSameDay(new Date(ev.startTime), dia));

  function abrirNovo(dia) {
    setDataParaNovo(format(dia, 'yyyy-MM-dd'));
    setNovoOpen(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setMesRef(m => subMonths(m, 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="text-lg font-semibold text-gray-800 w-44 text-center capitalize">
            {format(mesRef, 'MMMM yyyy', { locale: ptBR })}
          </span>
          <button onClick={() => setMesRef(m => addMonths(m, 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          onClick={() => abrirNovo(new Date())}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary"
        >
          <Plus size={15} /> Novo Compromisso
        </button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-400">Carregando...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
              <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {dias.map(dia => {
              const eventos = eventosNoDia(dia);
              const foraDoMes = !isSameMonth(dia, mesRef);
              return (
                <div
                  key={dia.toISOString()}
                  className={`min-h-24 border-b border-r border-gray-100 p-1.5 ${foraDoMes ? 'bg-gray-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isToday(dia) ? 'bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center' : foraDoMes ? 'text-gray-300' : 'text-gray-600'}`}>
                      {format(dia, 'd')}
                    </span>
                    <button
                      onClick={() => abrirNovo(dia)}
                      className="opacity-0 hover:opacity-100 text-gray-300 hover:text-primary transition-opacity"
                      title="Novo compromisso nesse dia"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {eventos.slice(0, 3).map(ev => (
                      <button
                        key={ev.id}
                        onClick={() => setDiaSelecionado(dia)}
                        className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate ${corDoEvento(ev.eventTypeId)}`}
                        title={ev.title}
                      >
                        {format(new Date(ev.startTime), 'HH:mm')} {ev.attendee_nome || ev.title}
                      </button>
                    ))}
                    {eventos.length > 3 && (
                      <button
                        onClick={() => setDiaSelecionado(dia)}
                        className="w-full text-left text-[10px] text-gray-400 px-1"
                      >
                        +{eventos.length - 3} mais
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <NovoCompromissoModal isOpen={novoOpen} onClose={() => setNovoOpen(false)} dataInicial={dataParaNovo} />
      <DiaDetalheModal
        isOpen={!!diaSelecionado}
        onClose={() => setDiaSelecionado(null)}
        dia={diaSelecionado}
        eventos={diaSelecionado ? eventosNoDia(diaSelecionado) : []}
      />
    </div>
  );
}
